import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, ProgressBar, InputGroup, Badge } from "react-bootstrap";
import { Formik } from "formik";
import { useNavigate, Link } from "react-router-dom";
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import Dashboardheader from './dashboardheader';
import Sidebar from './sidebar';
import config from '../../config/config';
import { rbacService } from '../../services'; // Using modern rbacService
import { BsArrowLeftCircleFill, BsCheck, BsShield, BsInfoCircle, BsFileText } from "react-icons/bs";
import { FaUserShield, FaCode, FaLevelUpAlt } from "react-icons/fa";
import { MdSecurity, MdAccountTree } from "react-icons/md";

import '../componentCss/webform.css';
import '../componentCss/sidebar.css';
import '../componentCss/healthcare-modern.css';
import 'react-toastify/dist/ReactToastify.css';

// New validation schema aligned with backend RoleCreate model
const addUserGroupSchema = Yup.object().shape({
  display_name: Yup.string()
    .required("Role name is required")
    .min(2, "Must be at least 2 characters")
    .max(100, "Cannot be longer than 100 characters"),
  role_id: Yup.string()
    .required("Role code is required")
    .min(3, "Must be at least 3 characters")
    .max(50, "Cannot be longer than 50 characters")
    .matches(/^[a-z0-9_-]+$/, "Code can only contain lowercase letters, numbers, underscores, and hyphens"),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Must be at least 10 characters")
    .max(500, "Cannot be longer than 500 characters"),
  hierarchy_level: Yup.number()
    .required("Hierarchy level is required")
    .min(1, "Hierarchy level must be between 1 and 10")
    .max(10, "Hierarchy level must be between 1 and 10"),
});

const AddUserGroup = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [formProgress, setFormProgress] = useState(0);
    const [existingRoles, setExistingRoles] = useState([]);
    
    // Updated initial form data to match backend schema
    const initialValues = {
        display_name: '', // Was 'name'
        role_id: '',      // Was 'code'
        description: '',
        hierarchy_level: 1,
    };

    // Fetch existing roles to show hierarchy levels
    useEffect(() => {
        const fetchExistingRoles = async () => {
            try {
                const res = await rbacService.getRoles();
                if (res.success) {
                    setExistingRoles(res.data || []);
                }
            } catch (error) {
            }
        };
        fetchExistingRoles();
    }, []);

    // Calculate form completion percentage
    const calculateFormCompletion = (values) => {
        const requiredFields = ['display_name', 'role_id', 'description', 'hierarchy_level'];
        const filledFields = requiredFields.filter(field => values[field] && values[field].toString().trim() !== '');
        return Math.round((filledFields.length / requiredFields.length) * 100);
    };

    // Generate role code from display name
    const generateRoleCode = (displayName) => {
        return displayName
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, '_')
            .trim();
    };

    // Check if role code already exists
    const isRoleCodeTaken = (roleId) => {
        return existingRoles.some(role => role.role_id === roleId);
    };

    // Get hierarchy level color
    const getHierarchyLevelColor = (level) => {
        if (level <= 3) return 'success';
        if (level <= 6) return 'warning';
        if (level <= 8) return 'info';
        return 'danger';
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        
        // Simulate progress updates
        const progressInterval = setInterval(() => {
            setFormProgress(prev => {
                if (prev < 90) return prev + 10;
                return prev;
            });
        }, 200);
        
        try {
            // Construct payload matching backend's RoleCreate schema
            const payload = {
                role_id: values.role_id,
                name: values.display_name, // Backend `name` is the display name
                display_name: values.display_name,
                description: values.description,
                hierarchy_level: values.hierarchy_level
            };

            const res = await rbacService.createRole(payload);
            
            clearInterval(progressInterval);
            setFormProgress(100);

            if (res.success) {
                toast.success(
                    <div>
                        <strong>Success!</strong><br/>
                        {res.message || "Role created successfully!"}
                    </div>
                );
                setTimeout(() => {
                    navigate(`${config.baseUrl}user-group`);
                }, 2000);
            } else {
                setIsLoading(false);
                setFormProgress(0);
                toast.error(
                    <div>
                        <strong>Error!</strong><br/>
                        {res.message || "Failed to create role."}
                    </div>
                );
            }
        } catch (error) {
            clearInterval(progressInterval);
            setIsLoading(false);
            setFormProgress(0);

            // Handle RBAC/Permission errors (403) explicitly
            const status = error?.response?.status;
            if (status === 403) {
                toast.error(error?.response?.data?.detail || 'You do not have permission to create roles.');
                return;
            }

            toast.error(
                <div>
                    <strong>Failed!</strong><br/>
                    {error.message || "An unexpected error occurred."}
                </div>
            );
        }
    };

    return (
        <>
            <Dashboardheader />
            <div className="wrapper healthcare-container">
                <Sidebar />
                <div id="content">
                    <Container fluid className="p-4">
                        <Row>
                            <Col lg={12} md={12} sm={12}>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <h5 className="headtitle mb-1">
                                            <FaUserShield className="me-2" />
                                            Create New Role
                                        </h5>
                                        <small className="text-muted">Define a new role with permissions and hierarchy level</small>
                                    </div>
                                    <Link to={`${config.baseUrl}user-group`}>
                                        <button className="healthcare-btn healthcare-btn-secondary">
                                            <BsArrowLeftCircleFill size={16} />
                                            Back to Roles
                                        </button>
                                    </Link>
                                </div>
                            </Col>
                        </Row>
                        
                        <Row>
                            <Col lg={8} md={10} sm={12}>
                                <div className="healthcare-card">
                                    <div className="p-4">
                                        <Formik
                                            initialValues={initialValues}
                                            validationSchema={addUserGroupSchema}
                                            onSubmit={handleSubmit}
                                        >
                                            {({
                                                errors,
                                                handleSubmit,
                                                handleBlur,
                                                handleChange,
                                                touched,
                                                values,
                                                setFieldValue,
                                            }) => {
                                                const completionPercentage = calculateFormCompletion(values);
                                                const generatedCode = generateRoleCode(values.display_name);
                                                const isCodeTaken = values.role_id && isRoleCodeTaken(values.role_id);
                                                
                                                return (
                                                    <Form onSubmit={handleSubmit}>
                                                        {/* Form Progress */}
                                                        <div className="mb-4">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span className="text-muted">Form Completion</span>
                                                                <span className="text-primary font-weight-bold">{completionPercentage}%</span>
                                                            </div>
                                                            <ProgressBar 
                                                                now={completionPercentage} 
                                                                variant={completionPercentage < 50 ? 'danger' : completionPercentage < 80 ? 'warning' : 'success'}
                                                                style={{ height: '8px' }}
                                                            />
                                                        </div>

                                                        {/* Role Information Section */}
                                                        <div className="mb-4">
                                                            <h6 className="text-primary mb-3 border-bottom pb-2">
                                                                <BsShield className="me-2" />
                                                                Role Information
                                                            </h6>
                                                        </div>

                                                        <Row className="mb-4">
                                                            <Form.Group as={Col} md="6">
                                                                <Form.Label>Role Name *</Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text>
                                                                        <FaUserShield size={16} />
                                                                    </InputGroup.Text>
                                                                    <Form.Control
                                                                        type="text"
                                                                        name="display_name"
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            // Auto-generate role code if not manually entered
                                                                            if (!values.role_id || values.role_id === generateRoleCode(values.display_name)) {
                                                                                setFieldValue('role_id', generateRoleCode(e.target.value));
                                                                            }
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        value={values.display_name}
                                                                        isInvalid={touched.display_name && !!errors.display_name}
                                                                        isValid={!errors.display_name && touched.display_name && values.display_name}
                                                                        placeholder="e.g., Senior Physician"
                                                                    />
                                                                </InputGroup>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.display_name}
                                                                </Form.Control.Feedback>
                                                                <Form.Text className="text-muted">
                                                                    Human-readable name that will be displayed in the interface
                                                                </Form.Text>
                                                            </Form.Group>

                                                            <Form.Group as={Col} md="6">
                                                                <Form.Label>Role Code *</Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text>
                                                                        <FaCode size={16} />
                                                                    </InputGroup.Text>
                                                                    <Form.Control
                                                                        type="text"
                                                                        name="role_id"
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        value={values.role_id}
                                                                        isInvalid={(touched.role_id && !!errors.role_id) || isCodeTaken}
                                                                        isValid={!errors.role_id && touched.role_id && values.role_id && !isCodeTaken}
                                                                        placeholder="e.g., senior_physician"
                                                                    />
                                                                    {generatedCode && generatedCode !== values.role_id && (
                                                                        <Button 
                                                                            variant="outline-secondary" 
                                                                            size="sm"
                                                                            onClick={() => setFieldValue('role_id', generatedCode)}
                                                                        >
                                                                            Use Generated
                                                                        </Button>
                                                                    )}
                                                                </InputGroup>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {isCodeTaken ? 'This role code is already taken' : errors.role_id}
                                                                </Form.Control.Feedback>
                                                                <Form.Text className="text-muted">
                                                                    Unique identifier for the role (lowercase, underscores allowed)
                                                                </Form.Text>
                                                            </Form.Group>
                                                        </Row>

                                                        <Row className="mb-4">
                                                            <Form.Group as={Col} md="8">
                                                                <Form.Label>Description *</Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text>
                                                                        <BsFileText size={16} />
                                                                    </InputGroup.Text>
                                                                    <Form.Control
                                                                        as="textarea"
                                                                        rows={3}
                                                                        name="description"
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        value={values.description}
                                                                        isInvalid={touched.description && !!errors.description}
                                                                        isValid={!errors.description && touched.description && values.description}
                                                                        placeholder="Describe the responsibilities and scope of this role..."
                                                                    />
                                                                </InputGroup>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.description}
                                                                </Form.Control.Feedback>
                                                                <Form.Text className="text-muted">
                                                                    Detailed description of the role's responsibilities and permissions
                                                                </Form.Text>
                                                            </Form.Group>
                                                            
                                                            <Form.Group as={Col} md="4">
                                                                <Form.Label>Hierarchy Level *</Form.Label>
                                                                <InputGroup>
                                                                    <InputGroup.Text>
                                                                        <FaLevelUpAlt size={16} />
                                                                    </InputGroup.Text>
                                                                    <Form.Control
                                                                        type="number"
                                                                        name="hierarchy_level"
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        value={values.hierarchy_level}
                                                                        isInvalid={touched.hierarchy_level && !!errors.hierarchy_level}
                                                                        isValid={!errors.hierarchy_level && touched.hierarchy_level && values.hierarchy_level}
                                                                        min="1"
                                                                        max="10"
                                                                    />
                                                                    <InputGroup.Text>
                                                                        <Badge bg={getHierarchyLevelColor(values.hierarchy_level)}>
                                                                            Level {values.hierarchy_level}
                                                                        </Badge>
                                                                    </InputGroup.Text>
                                                                </InputGroup>
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.hierarchy_level}
                                                                </Form.Control.Feedback>
                                                                <Form.Text className="text-muted">
                                                                    1 = Basic, 10 = Highest Authority
                                                                </Form.Text>
                                                            </Form.Group>
                                                        </Row>

                                                        {/* Existing Roles Reference */}
                                                        {existingRoles.length > 0 && (
                                                            <Alert variant="info" className="mb-4">
                                                                <BsInfoCircle className="me-2" />
                                                                <strong>Existing Hierarchy Levels:</strong>
                                                                <div className="mt-2">
                                                                    {existingRoles
                                                                        .sort((a, b) => b.hierarchy_level - a.hierarchy_level)
                                                                        .slice(0, 5)
                                                                        .map((role, index) => (
                                                                            <Badge 
                                                                                key={index}
                                                                                bg={getHierarchyLevelColor(role.hierarchy_level)}
                                                                                className="me-2 mb-1"
                                                                            >
                                                                                {role.display_name || role.name} (Level {role.hierarchy_level})
                                                                            </Badge>
                                                                        ))
                                                                    }
                                                                    {existingRoles.length > 5 && (
                                                                        <span className="text-muted">... and {existingRoles.length - 5} more</span>
                                                                    )}
                                                                </div>
                                                            </Alert>
                                                        )}

                                                        {/* Submit Section */}
                                                        <div className="border-top pt-4 mt-4">
                                                            <Row>
                                                                <Col lg={6}>
                                                                    {isLoading && (
                                                                        <div className="mb-3">
                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                <span className="text-muted">Creating role...</span>
                                                                                <span className="text-primary">{formProgress}%</span>
                                                                            </div>
                                                                            <ProgressBar now={formProgress} variant="primary" style={{ height: '6px' }} />
                                                                        </div>
                                                                    )}
                                                                    <div className="d-flex gap-3">
                                                                        <button
                                                                            type="submit"
                                                                            disabled={isLoading || isCodeTaken}
                                                                            className="healthcare-btn healthcare-btn-primary"
                                                                        >
                                                                            {isLoading ? (
                                                                                <>
                                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                                    Creating...
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <BsCheck size={16} />
                                                                                    Create Role
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                        <Link to={`${config.baseUrl}user-group`}>
                                                                            <button 
                                                                                type="button"
                                                                                disabled={isLoading}
                                                                                className="healthcare-btn healthcare-btn-secondary"
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </Link>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    </Form>
                                                );
                                            }}
                                        </Formik>
                                    </div>
                                </div>
                            </Col>
                            
                            {/* Help Panel */}
                            <Col lg={4} md={2} sm={12}>
                                <div className="healthcare-card">
                                    <div className="p-4">
                                        <h6 className="mb-3">
                                            <MdAccountTree className="me-2" />
                                            Hierarchy Guide
                                        </h6>
                                        <div className="small">
                                            <div className="mb-2">
                                                <Badge bg="success" className="me-2">1-3</Badge>
                                                Basic roles (Staff, Assistant)
                                            </div>
                                            <div className="mb-2">
                                                <Badge bg="warning" className="me-2">4-6</Badge>
                                                Supervisory roles (Manager, Supervisor)
                                            </div>
                                            <div className="mb-2">
                                                <Badge bg="info" className="me-2">7-8</Badge>
                                                Senior roles (Director, Senior Manager)
                                            </div>
                                            <div className="mb-2">
                                                <Badge bg="danger" className="me-2">9-10</Badge>
                                                Executive roles (CEO, CTO)
                                            </div>
                                        </div>
                                        
                                        <hr />
                                        
                                        <h6 className="mb-3">
                                            <MdSecurity className="me-2" />
                                            Best Practices
                                        </h6>
                                        <ul className="small list-unstyled">
                                            <li className="mb-2">• Use descriptive role names</li>
                                            <li className="mb-2">• Keep role codes lowercase</li>
                                            <li className="mb-2">• Set appropriate hierarchy levels</li>
                                            <li className="mb-2">• Include clear descriptions</li>
                                        </ul>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </div>
        </>
    );
};

export default AddUserGroup;
